import {
  LazyQueryResult,
  OperationVariables,
  useLazyQuery,
  useMutation,
} from '@apollo/client'
import {
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  MenuItem,
} from '@mui/material'
import { Query, StripePaymentIntent } from '@promoboxx/graphql-gateway-types'
import {
  // PaymentElement, // this is the new component that will replace the StripePaymentForm once automatic payment methods are enabled
  useStripe,
  useElements,
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import { StripeElementChangeEvent } from '@stripe/stripe-js'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import {
  ForwardedRef,
  ReactElement,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import acceptedCountries, {
  AcceptedCountry,
} from '@src/components/payment/acceptedCountries'
import Loading from '@src/components/shared/Loading'
import useRetailerAccountWallet from '@src/components/wallet/useRetailerAccountWallet'
import { formatWithCents } from '@src/lib/Utils'

import stripePromiseRef, {
  UPDATE_PAYMENT_INTENT,
  GET_PAYMENT_INTENT,
} from './stripe'

const POLL_INTERVAL = 5000 // 5 second interval
const TIMEOUT = 60000 // 60 seconds

async function pollPaymentStatus(
  getPaymentIntentStatus: () => Promise<
    LazyQueryResult<Query, OperationVariables>
  >,
  endTime: number,
): Promise<string> {
  // Check for timeout
  if (Date.now() > endTime) {
    throw new Error('polling_timeout')
  }

  // Request status from GraphQL endpoint, SUCCEEDED, CREATED, PROCESSING, FAILED, UNKNOWN should be only values returned
  const { data } = await getPaymentIntentStatus()
  const status = data?.getStripePaymentIntentByID.status
  if (status && !['UNKNOWN', 'CREATED', 'PROCESSING'].includes(status)) {
    return status
  }

  // Wait for the next poll and then recursively call pollPaymentStatus
  await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL))
  return pollPaymentStatus(getPaymentIntentStatus, endTime)
}

interface StripePaymentFormProps {
  paymentIntent: StripePaymentIntent
  updateIntent: (intent: StripePaymentIntent) => void
  onComplete: () => void
  onBack: () => void
  onFail: (reason: string) => void
}

const LoaderBox: React.FC<React.PropsWithChildren> = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <Loading />
  </Box>
)

// Stripe requires Elements provider
const withStripeProvider =
  <T extends StripePaymentFormProps>(
    Component: React.ComponentType<React.PropsWithChildren<T>>,
  ) =>
  (props: T) => {
    return props.paymentIntent ? (
      <Elements
        stripe={stripePromiseRef.instance()}
        options={{
          mode: 'payment',
          amount: Number(props?.paymentIntent?.final_charge_amount),
          currency: 'usd',
          loader: 'always',
        }}
      >
        <Component {...props} />
      </Elements>
    ) : (
      <LoaderBox />
    )
  }

const schema = Yup.object().shape({
  cardCvc: Yup.string().required().label(I18n.t('content.payment.card.cvc')),
  cardNumber: Yup.string()
    .required()
    .label(I18n.t('content.payment.card.number')),
  cardExpiry: Yup.string()
    .required()
    .label(I18n.t('content.payment.card.expiration')),
  countryCode: Yup.string().required(),
  postalCode: Yup.string()
    .required()
    .label(I18n.t('content.payment.card.billingZip'))
    .test('is-valid-postal-code', 'Invalid postal code', function (value) {
      const { countryCode } = this.parent
      const pattern = acceptedCountries.find(
        (country: AcceptedCountry) => country.code === countryCode,
      )?.postalRegex
      if (pattern && value) {
        return pattern.test(value)
      }
      return pattern ? false : true
    }),
})

const StripePaymentForm: React.FC<
  React.PropsWithChildren<StripePaymentFormProps>
> = ({ paymentIntent, onComplete, onBack, onFail, updateIntent }) => {
  const currentUser = useSelector((state) => state.auth.user)
  const stripe = useStripe()
  const elements = useElements()
  const [getPaymentStatus] = useLazyQuery(GET_PAYMENT_INTENT, {
    fetchPolicy: 'network-only',
  })
  const [updatePaymentIntent] = useMutation(UPDATE_PAYMENT_INTENT, {
    fetchPolicy: 'network-only',
  })
  const [isBusy, setIsBusy] = useState(false)
  const retailerWallet = useRetailerAccountWallet()

  if (!stripe || !elements) {
    return <LoaderBox />
  }

  return (
    <Formik
      initialValues={{
        cardCvc: '',
        cardExpiry: '',
        cardNumber: '',
        countryCode: 'US',
        postalCode: '',
      }}
      validationSchema={schema}
      onSubmit={async (values, formik) => {
        try {
          const card = elements.getElement(CardNumberElement) // Stripe automatically handles the other elements for fetching data
          const expiry = elements.getElement(CardExpiryElement)
          const cvc = elements.getElement(CardCvcElement)
          card?.update({ disabled: true })
          expiry?.update({ disabled: true })
          cvc?.update({ disabled: true })

          if (!card) {
            throw new Error('missing_card_element')
          }

          const { error } = await stripe.confirmCardPayment(
            paymentIntent.client_secret,
            {
              payment_method: {
                card,
                billing_details: {
                  address: {
                    country: values.countryCode,
                    postal_code: values.postalCode,
                  },
                },
              },
              receipt_email: currentUser.email,
            },
          )
          if (error) {
            card?.update({ disabled: false })
            expiry?.update({ disabled: false })
            cvc?.update({ disabled: false })
            // update the correct mui field with card decline error
            switch (error.code) {
              case 'incorrect_cvc':
                formik.setFieldError('cardCvc', `${error.message}`)
                break
              case 'expired_card':
                formik.setFieldError('cardExpiry', `${error.message}`)
                break
              case 'incorrect_zip':
                formik.setFieldError('postalCode', `${error.message}`)
                break
              default:
                formik.setFieldError('cardNumber', `${error.message}`)
                break
            }
            if (error.code === 'processing_error') {
              throw new Error(error.code)
            }
            throw new Error(error.type)
          } else {
            const status = await pollPaymentStatus(
              () => getPaymentStatus({ variables: { id: paymentIntent.id } }),
              Date.now() + TIMEOUT,
            )

            // TODO: change to updated BE values
            if (status === 'SUCCEEDED') {
              retailerWallet.refetchWallet()
              onComplete()
            } else if (status === 'FAILED') {
              throw new Error('payment_canceled')
            } else {
              throw new Error('payment_processing')
            }
          }
        } catch (e) {
          formik.setSubmitting(false)
          if (e instanceof Error) {
            onFail(e.message)
          }
        }
      }}
    >
      {({
        values,
        isSubmitting,
        isValid,
        setFieldError,
        setFieldValue,
        setFieldTouched,
      }) => (
        <Form>
          <Stack direction="column" sx={{ paddingTop: 4 }} spacing={2}>
            <Typography align="center" variant="h3">
              {formatWithCents.format(Number(paymentIntent.amount) / 100)}
            </Typography>
            <Button variant="text" disabled={isSubmitting} onClick={onBack}>
              {I18n.t('content.payment.changeAmount')}
            </Button>
            {/* When Automatic Payment methods are enabled this stack and its children can be replaced with a single PaymentElement */}
            <Stack direction="column" spacing={2}>
              <Field
                component={TextField}
                label={I18n.t('content.payment.card.number')}
                name="cardNumber"
                variant="outlined"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  inputComponent: StripeInput,
                  inputProps: {
                    component: CardNumberElement,
                    onBlur: (e: StripeElementChangeEvent) => {
                      setFieldTouched('cardNumber')
                    },
                    onChange: (e: StripeElementChangeEvent) => {
                      if (e.error) {
                        setFieldError('cardNumber', e.error.message)
                      } else {
                        setFieldValue(
                          'cardNumber',
                          e.complete ? 'complete' : '',
                        )
                      }
                    },
                  },
                }}
              />

              <Stack direction="row" spacing={2} alignItems="center">
                <Field
                  component={TextField}
                  label={I18n.t('content.payment.card.expiration')}
                  name="cardExpiry"
                  variant="outlined"
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputComponent: StripeInput,
                    inputProps: {
                      component: CardExpiryElement,
                      onBlur: () => {
                        setFieldTouched('cardExpiry')
                      },
                      onChange: (e: StripeElementChangeEvent) => {
                        setFieldValue(
                          'cardExpiry',
                          e.complete ? 'complete' : '',
                        )
                        if (e.error) {
                          setFieldError('cardExpiry', e.error.message)
                        }
                      },
                    },
                  }}
                />

                <Field
                  component={TextField}
                  label={I18n.t('content.payment.card.cvc')}
                  name="cardCvc"
                  variant="outlined"
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputComponent: StripeInput,
                    inputProps: {
                      component: CardCvcElement,
                      onBlur: () => {
                        setFieldTouched('cardCvc')
                      },
                      onChange: (e: StripeElementChangeEvent) => {
                        setFieldValue('cardCvc', e.complete ? 'complete' : '')
                        if (e.error) {
                          setFieldError('cardCvc', e.error.message)
                        }
                      },
                    },
                  }}
                />
              </Stack>
              <Field
                component={TextField}
                name="countryCode"
                type="text"
                label="Country Code"
                variant="outlined"
                required
                InputLabelProps={{ shrink: true }}
                placeholder="US"
                fullWidth
                select
                value={values.countryCode}
                disabled={isBusy || isSubmitting}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue('countryCode', e.target.value)
                  setIsBusy(true)
                  updatePaymentIntent({
                    variables: {
                      data: {
                        amount_cents: paymentIntent.amount,
                        id: paymentIntent.id,
                        country_code: e.target.value,
                      },
                    },
                  })
                    .then(({ data }) => {
                      updateIntent(data.updatePaymentIntent)
                      setIsBusy(false)
                    })
                    .catch((e) => {
                      setIsBusy(false)
                      onFail(e.message)
                    })
                }}
              >
                {acceptedCountries.map((country: AcceptedCountry) => (
                  <MenuItem
                    key={`country-${country.code}}`}
                    value={country.code}
                  >
                    {country.name}
                  </MenuItem>
                ))}
              </Field>
              <Field
                component={TextField}
                name="postalCode"
                type="text"
                label="Postal Code"
                variant="outlined"
                required
                InputLabelProps={{ shrink: true }}
                placeholder="12345"
                fullWidth
              />
            </Stack>
            <Typography variant="h6" align="center">
              {I18n.t('content.payment.depositDisclaimer')}
            </Typography>
            <Button
              type="submit"
              color="primary"
              fullWidth
              variant="contained"
              disabled={isSubmitting || !isValid || isBusy}
            >
              {isSubmitting ? (
                <CircularProgress />
              ) : (
                I18n.t('content.payment.payAmount', {
                  amount: formatWithCents.format(
                    Number(paymentIntent?.final_charge_amount) / 100,
                  ),
                })
              )}
            </Button>
            <Typography variant="caption" align="center">
              {I18n.t('content.payment.poweredByStripe')}
            </Typography>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}

export default withStripeProvider(StripePaymentForm)

// StripeInput is a wrapper around the Stripe Elements components that allows us to use Material UI's TextField component
// This code will no longer be needed when we switch to PaymentElement
interface StripeInputProps {
  component: React.ComponentType<React.PropsWithChildren<any>>
  inputRef: ForwardedRef<any>
  [key: string]: any
}

const StripeInput: React.FC<React.PropsWithChildren<StripeInputProps>> = ({
  component: Component,
  inputRef,
  ...props
}) => {
  const elementRef = useRef<any>(null)

  useImperativeHandle(inputRef, () => ({
    focus: () => elementRef.current.focus(),
  }))

  return (
    <Component
      onReady={(element: ReactElement) => (elementRef.current = element)}
      {...props}
    />
  )
}
